import {DayOfWeek, LocalDate, nativeJs} from "@js-joda/core";
import useApiFetch, {apiEndpoint} from "@tracker/shared/src/Auth/Api";
import {FirstDayOfWeek, usePreferencesState} from "@tracker/shared/src/Preferences/PreferencesProvider";
import {useSnackbar} from 'notistack';
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import type {ReactElement, ReactNode} from "react";
import {TimeEntry} from "../pages/TimeEntries";
import {mapRawTimeEntry} from "../utils/mapRawTimeEntry";

type TimeTotals = Record<FirstDayOfWeek, number> & {
    lastWeek : number;
    loaded : boolean;
};

type UnsubmittedType = {
    timeTotals : TimeTotals,
    refreshMissingTime : () => Promise<void>,
    refreshMissingTimeIfNeeded : (date : LocalDate) => Promise<void>,
    updateEntry : (time : TimeEntry) => void,
    removeEntry : (time : TimeEntry) => void,
    addEntry : (time : TimeEntry) => void,
}

const emptyTotals = {
    loaded: false,
    lastWeek: 0,
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
};

const MissingTimeContext = createContext<UnsubmittedType>({
    timeTotals: emptyTotals,
    /* eslint-disable @typescript-eslint/no-empty-function */
    refreshMissingTime: async () => {},
    refreshMissingTimeIfNeeded: async () => {},
    updateEntry: () => {},
    removeEntry: () => {},
    addEntry: () => {},
    /* eslint-enable @typescript-eslint/no-empty-function */
});

export const useMissingTimeProvider = () : UnsubmittedType => {
    const context = useContext(MissingTimeContext);

    if (Object.keys(context).length === 0) {
        throw new Error('useMissingTimeProvider must be used within a MissingTimeProviderProvider');
    }

    return context;
};

const getDateForXthDayOfLastWeek = (dayOfWeek : number) => {
    const today = new Date();
    const day = today.getDay();
    const diff = (day + 7 - dayOfWeek) % 7 + 7;
    const lastWeekDate = new Date(today);
    lastWeekDate.setDate(today.getDate() - diff);
    return lastWeekDate;
}

export const MissingTimeProvider = ({children} : {children : ReactNode}) : ReactElement => {
    const {preferences} = usePreferencesState();
    const [entries, setEntries] = useState<TimeEntry[]>([]);
    const apiFetch = useApiFetch();
    const {enqueueSnackbar} = useSnackbar();
    const startDate = useMemo(() => {
        return LocalDate.from(nativeJs(
            getDateForXthDayOfLastWeek(preferences.locale.firstDayOfWeek)
        ));
    }, [preferences]);

    const updateEntry = useCallback((time : TimeEntry) => {
        const newEntries = [
            ...entries
        ];
        newEntries[newEntries.findIndex(e => e.id === time.id)] = time;
        setEntries(newEntries);
    },[entries]);

    const removeEntry = useCallback((time : TimeEntry) => {
        setEntries(entries.filter(entry => entry.id !== time.id));
    },[entries]);

    const addEntry = useCallback((time : TimeEntry) => {
        const newEntries = [
            ...entries,
            time,
        ];
        setEntries(newEntries);
    },[entries]);

    const endDate = useMemo(() => {
        if (preferences.timeEntry.warnLowHoursThisWeek) {
            const endDate = new Date();
            endDate.setDate(endDate.getDate() - 1);
            return LocalDate.from(nativeJs(
                endDate
            ));
        }

        return startDate.plusWeeks(1);
    }, [preferences.timeEntry.warnLowHoursThisWeek, startDate]);

    const refreshMissingTime = useCallback(async () => {
        const url = new URL('/v1/time-entries', apiEndpoint);
        url.searchParams.set('dateStart', startDate.toString());
        url.searchParams.set('dateEnd', endDate.toString());

        try {
            const response = await apiFetch(url.toString());
            const data = await response.json();
            setEntries(data.map(mapRawTimeEntry));
        } catch(e) {
            enqueueSnackbar('An error occurred while fetching time entries.', {variant: 'error'});
            return;
        }
    }, [apiFetch, endDate, enqueueSnackbar, startDate]);

    const timeTotals = useMemo(() => {
        const totals : TimeTotals = {
            loaded: true,
            lastWeek: 0,
            0: 0,
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0,
            6: 0,
        };

        const startOfThisWeek = startDate.plusWeeks(1);
        const today = LocalDate.now()

        for (const entry of entries) {
            const minutes = entry.minutes ?? 0;
            if (startDate.compareTo(entry.startDate) > 0) {
                continue;//too old don't care
            }
            if (startOfThisWeek.compareTo(entry.startDate) >= 0) {
                totals.lastWeek += minutes;
                continue;
            }
            if (today.compareTo(entry.startDate) < 0) {
                continue;//not in the past don't care
            }
            const dayOfWeek = entry.startDate.dayOfWeek()
            switch (dayOfWeek) {
                case DayOfWeek.SUNDAY:
                    totals[0] += minutes;
                    break;
                case DayOfWeek.MONDAY:
                    totals[1] += minutes;
                    break;
                case DayOfWeek.TUESDAY:
                    totals[2] += minutes;
                    break;
                case DayOfWeek.WEDNESDAY:
                    totals[3] += minutes;
                    break;
                case DayOfWeek.THURSDAY:
                    totals[4] += minutes;
                    break;
                case DayOfWeek.FRIDAY:
                    totals[5] += minutes;
                    break;
                case DayOfWeek.SATURDAY:
                    totals[6] += minutes;
                    break;
            }
        }
        return totals;
    }, [entries, startDate]);

    const refreshMissingTimeIfNeeded = useCallback(async (timeEntry : LocalDate) => {
        if (endDate.compareTo(timeEntry) > 0) {
            await refreshMissingTime();
        }
    }, [endDate, refreshMissingTime])

    useEffect(() => {
        refreshMissingTime().catch(e => {
            console.error('refreshMissingTime', e);
            enqueueSnackbar('An error occurred while fetching time entries.', {variant: 'error'});
        });
    }, [startDate, endDate, refreshMissingTime, enqueueSnackbar]);

    return (<MissingTimeContext.Provider value={{
        timeTotals,
        refreshMissingTime,
        refreshMissingTimeIfNeeded,
        updateEntry,
        removeEntry,
        addEntry,
    }}>
        {children}
    </MissingTimeContext.Provider>)
}
